import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { NotificationListResponse, notificationService } from '@/services/notificationService';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: () => [...notificationKeys.all, 'list'] as const,
  byPoolId: (poolId: number) => [...notificationKeys.all, 'pool', poolId] as const,
};

export function useInfiniteNotifications({ isAuthenticated }: { isAuthenticated?: boolean }) {
  return useInfiniteQuery<NotificationListResponse, Error>({
    queryKey: notificationKeys.list(),
    queryFn: async ({ pageParam }) => {
      const result = await notificationService.getNotifications({
        page: pageParam as number,
        is_read: false,
      });
      if (result.error) throw new Error(result.error.message);
      if (!result.data) throw new Error('No data returned');
      return result.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.pagination.has_next ? lastPage.pagination.page + 1 : undefined),
    gcTime: 0,
    staleTime: 0,
    enabled: isAuthenticated,
  });
}

/**
 * Mark all notifications as read mutation
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const result = await notificationService.markAllRead();
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
    },
  });
}

/**
 * Get pool notification
 */

export function useNotificationsByPoolId(poolId: number) {
  return useQuery({
    queryKey: notificationKeys.byPoolId(poolId),
    queryFn: async () => {
      const result = await notificationService.getNotificationsByPoolId(poolId);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    gcTime: 0,
    staleTime: 0,
  });
}

/**
 * Mark pool notification as read mutation
 */
export function useMarkPoolNotificationAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (poolId: number) => {
      const result = await notificationService.markPoolNotificationAsRead(poolId);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Call "mark pool notifications as read" once when the user lands on a pool detail page.
 * Encapsulates the effect + ref so the page doesn't run the mutation in an effect directly.
 */
export function useMarkPoolNotificationsReadOnVisit(poolId: string | undefined) {
  const markAsRead = useMarkPoolNotificationAsRead();
  const hasMarked = useRef(false);

  useEffect(() => {
    if (poolId) hasMarked.current = false;
  }, [poolId]);

  useEffect(() => {
    if (!poolId || hasMarked.current) return;
    const id = Number(poolId);
    if (!id) return;
    hasMarked.current = true;
    markAsRead.mutate(id);
  }, [poolId, markAsRead]);
}
